<template>
  <div class="push-setup">
    <!-- Not supported -->
    <div v-if="!isSupported" class="push-message">
      <p>Push notifications are not supported in this browser.</p>
    </div>

    <!-- Build is missing the VAPID public key -->
    <div v-else-if="!isConfigured" class="push-message">
      <p>Push notifications are not available right now. Please check back later.</p>
    </div>

    <!-- iOS: needs install to home screen first -->
    <div v-else-if="needsIOSInstall">
      <p class="push-message">To receive push notifications on iOS, you need to install this app to your home screen first.</p>
      <button type="button" class="button-push" @click="showIOSGuide = true">How to Install</button>
      <ios-install-guide v-if="showIOSGuide" @close="showIOSGuide = false"></ios-install-guide>
    </div>

    <!-- Permission denied -->
    <div v-else-if="permissionState === 'denied'" class="push-message">
      <p>Push notifications are blocked. To enable them, open your browser settings and allow notifications for this site.</p>
    </div>

    <!-- Ready to subscribe / manage -->
    <div v-else>
      <!-- Android install prompt -->
      <div v-if="canInstallAndroid" class="install-section">
        <button type="button" class="button-push" @click="installAndroid()">Install App</button>
        <p class="help-text">For the best experience</p>
      </div>

      <!-- Standalone success message -->
      <div v-if="justBecameStandalone && !isSubscribed" class="success-message">
        App installed successfully! Now enable push notifications below.
      </div>

      <!-- Subscribe button -->
      <div v-if="!isSubscribed">
        <button type="button" class="button-push" @click="subscribe()" :disabled="subscribing">
          {{ subscribing ? 'Enabling...' : 'Enable Push Notifications' }}
        </button>
      </div>

      <!-- Already subscribed -->
      <div v-if="isSubscribed" class="success-message">
        Push notifications enabled on this device.
      </div>

      <!-- Device list -->
      <div v-if="devices.length > 0" class="device-section">
        <p class="device-header">Registered devices ({{ devices.length }}):</p>
        <ul class="device-list">
          <li v-for="(device, index) in devices" :key="device.id">
            <span class="device-name">{{ deviceLabel(device) }}</span>
            <button type="button" class="button-remove" @click="removeDevice(device)">Remove</button>
          </li>
        </ul>
      </div>

      <!-- Error -->
      <p v-if="error" class="error-message">{{ error }}</p>
    </div>
  </div>
</template>

<script>
import PushRepository from '../data/push.repository'
import IOSInstallGuide from './IOSInstallGuide'

// VAPID_PUBLIC_KEY is injected by webpack DefinePlugin and falls back to ''
// when the build env var is missing; typeof guards builds without the define.
function getVapidPublicKey () {
  return (typeof VAPID_PUBLIC_KEY !== 'undefined' && VAPID_PUBLIC_KEY) || '' // eslint-disable-line no-undef
}

function urlBase64ToUint8Array (base64String) {
  var padding = '='.repeat((4 - base64String.length % 4) % 4)
  var base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  var rawData = window.atob(base64)
  var outputArray = new Uint8Array(rawData.length)
  for (var i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

export default {
  name: 'PushSetup',
  components: {
    'ios-install-guide': IOSInstallGuide
  },
  data () {
    return {
      isSupported: false,
      isConfigured: true,
      isSubscribed: false,
      permissionState: 'default',
      subscribing: false,
      devices: [],
      error: '',
      showIOSGuide: false,
      canInstallAndroid: false,
      justBecameStandalone: false
    }
  },
  computed: {
    isIOS () {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
    },
    isStandalone () {
      return window.navigator.standalone === true ||
        window.matchMedia('(display-mode: standalone)').matches
    },
    needsIOSInstall () {
      return this.isIOS && !this.isStandalone
    }
  },
  methods: {
    async loadDevices () {
      try {
        this.devices = await PushRepository.getSubscriptions()
      } catch (e) {
        // ignore
      }
    },
    async checkSubscription () {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return
      try {
        var reg = await navigator.serviceWorker.ready
        var sub = await reg.pushManager.getSubscription()
        this.isSubscribed = !!sub
      } catch (e) {
        // ignore
      }
    },
    async subscribe () {
      this.error = ''
      var vapidKey = getVapidPublicKey()
      if (!vapidKey) {
        this.isConfigured = false
        return
      }
      this.subscribing = true
      try {
        var reg = await navigator.serviceWorker.ready
        var sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey)
        })
        var subJson = sub.toJSON()
        await PushRepository.subscribe({
          endpoint: subJson.endpoint,
          p256dh: subJson.keys.p256dh,
          auth: subJson.keys.auth
        })
        this.isSubscribed = true
        this.permissionState = 'granted'
        await this.loadDevices()
        this.$emit('subscribed')
      } catch (e) {
        if (Notification.permission === 'denied') {
          this.permissionState = 'denied'
        } else {
          this.error = 'Could not enable push notifications. Please try again.'
        }
      }
      this.subscribing = false
    },
    async removeDevice (device) {
      try {
        await PushRepository.unsubscribe(device.endpoint)
        this.devices = this.devices.filter(function (d) { return d.id !== device.id })
        // Check if current device was removed
        await this.checkSubscription()
      } catch (e) {
        this.error = 'Could not remove device. Please try again.'
      }
    },
    deviceLabel (device) {
      var label = 'Device ' + (this.devices.indexOf(device) + 1)
      if (device.user_agent) {
        if (device.user_agent.indexOf('iPhone') > -1) label = 'iPhone'
        else if (device.user_agent.indexOf('iPad') > -1) label = 'iPad'
        else if (device.user_agent.indexOf('Android') > -1) label = 'Android'
        else if (device.user_agent.indexOf('Mac') > -1) label = 'Mac'
        else if (device.user_agent.indexOf('Windows') > -1) label = 'Windows'
        else if (device.user_agent.indexOf('Linux') > -1) label = 'Linux'
      }
      var linked = this.linkedDate(device.created_at)
      return linked ? label + ' - ' + linked : label
    },
    linkedDate (createdAt) {
      // created_at arrives as "YYYY-MM-DD HH:MM:SS" (UTC); Safari can't
      // Date-parse that format, so pull the date apart by hand.
      var parts = String(createdAt || '').slice(0, 10).split('-')
      if (parts.length !== 3) return ''
      return parseInt(parts[1], 10) + '/' + parseInt(parts[2], 10) + '/' + parts[0].slice(2)
    },
    async installAndroid () {
      if (window.deferredInstallPrompt) {
        window.deferredInstallPrompt.prompt()
        var result = await window.deferredInstallPrompt.userChoice
        if (result.outcome === 'accepted') {
          this.canInstallAndroid = false
        }
        window.deferredInstallPrompt = null
      }
    }
  },
  mounted () {
    // iOS Safari only exposes PushManager inside an installed home-screen
    // app, so an iOS browser tab without PushManager still counts as
    // supported — the user just needs to install to the home screen first.
    var hasPushApi = 'serviceWorker' in navigator && 'PushManager' in window
    this.isSupported = hasPushApi || (this.isIOS && !this.isStandalone)
    if (!this.isSupported) return

    this.isConfigured = getVapidPublicKey().length > 0
    if (!this.isConfigured) return

    this.permissionState = ('Notification' in window) ? Notification.permission : 'default'
    this.canInstallAndroid = !!window.deferredInstallPrompt

    // Detect if user just came from installing as PWA
    if (this.isStandalone && this.isIOS) {
      this.justBecameStandalone = true
    }

    this.checkSubscription()
    this.loadDevices()
  }
}
</script>

<style scoped>
.push-setup {
  margin-top: 8px;
  padding: 12px;
  background: #f5f5f7;
  border-radius: 4px;
}

.push-message {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  margin-bottom: 8px;
}

.install-section {
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e3;
}

.help-text {
  font-size: 11px;
  color: #999;
  margin-top: 6px;
  margin-bottom: 0;
}

.button-push {
  background: #e94560;
  border: none;
  color: #fff;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: bold;
  width: 100%;
}

.button-push:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.success-message {
  font-size: 12px;
  color: #27ae60;
  background: #e8f8f0;
  padding: 8px 10px;
  border-radius: 4px;
  margin-bottom: 8px;
  line-height: 1.4;
}

.error-message {
  color: #e94560;
  font-size: 12px;
  margin-top: 8px;
  background: #ffe8ec;
  padding: 8px 10px;
  border-radius: 4px;
}

.device-section {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e0e0e3;
}

.device-header {
  font-size: 12px;
  color: #666;
  margin-bottom: 6px;
}

.device-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.device-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #e9e9ed;
  font-size: 12px;
}

.device-list li:last-child {
  border-bottom: none;
}

.device-name {
  color: #5e636d;
  font-weight: bold;
}

.button-remove {
  background: #fff;
  border: 1px solid #e94560;
  color: #e94560;
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// PushSetup.vue?12f03cfc